import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  ArrowRight,
  CreditCard,
  Loader2,
  Percent,
  RefreshCw,
  Ribbon,
  Sparkles,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { feesApi } from '../api/services.js'
import PageHeader from '../components/PageHeader.jsx'

const formatCurrency = (value = 0) => `₹${Number(value || 0).toLocaleString('en-IN')}`

const yearSort = (left, right) => right.localeCompare(left, undefined, { numeric: true, sensitivity: 'base' })

export default function FeeAnalyticsPage() {
  const navigate = useNavigate()
  const [summary, setSummary] = useState(null)
  const [defaulters, setDefaulters] = useState([])
  const [years, setYears] = useState([])
  const [selectedYear, setSelectedYear] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      const params = selectedYear ? { academicYear: selectedYear } : undefined
      const [summaryRes, defaultersRes, structuresRes] = await Promise.allSettled([
        feesApi.getSummary(params),
        feesApi.getDefaulters(params),
        feesApi.getStructures(),
      ])

      if (summaryRes.status === 'fulfilled') {
        setSummary(summaryRes.value || null)
      } else {
        setSummary(null)
      }

      if (defaultersRes.status === 'fulfilled') {
        setDefaulters(Array.isArray(defaultersRes.value) ? defaultersRes.value : [])
      } else {
        setDefaulters([])
      }

      if (structuresRes.status === 'fulfilled') {
        const availableYears = [...new Set((structuresRes.value || []).map((item) => item.academicYear).filter(Boolean))].sort(yearSort)
        setYears(availableYears)
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load fee analytics')
      setSummary(null)
      setDefaulters([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAnalytics()
  }, [selectedYear])

  const statCards = useMemo(() => {
    if (!summary) return []
    return [
      {
        label: 'Total Demand',
        value: formatCurrency(summary.totalDemand),
        icon: Wallet,
        color: 'bg-primary-50 text-primary-700',
        note: selectedYear || 'All academic years',
      },
      {
        label: 'Collected',
        value: formatCurrency(summary.totalCollected),
        icon: TrendingUp,
        color: 'bg-green-50 text-green-700',
        note: `${Number(summary.collectionPercent || 0).toFixed(1)}% collection rate`,
      },
      {
        label: 'Outstanding',
        value: formatCurrency(summary.totalPending),
        icon: AlertCircle,
        color: 'bg-red-50 text-red-600',
        note: `${summary.statusCounts?.overdue || 0} overdue accounts`,
      },
      {
        label: 'Net Demand',
        value: formatCurrency(summary.netDemand ?? summary.totalDemand),
        icon: CreditCard,
        color: 'bg-violet-50 text-violet-700',
        note: `${summary.totalStudents || 0} student accounts`,
      },
    ]
  }, [selectedYear, summary])

  const statusRows = useMemo(
    () => [
      { label: 'Fully Paid', count: summary?.statusCounts?.paid || 0, color: 'bg-green-500' },
      { label: 'Partial', count: summary?.statusCounts?.partial || 0, color: 'bg-amber-500' },
      { label: 'Pending', count: summary?.statusCounts?.pending || 0, color: 'bg-slate-400' },
      { label: 'Overdue', count: summary?.statusCounts?.overdue || 0, color: 'bg-red-500' },
    ],
    [summary]
  )

  const maxStatusCount = Math.max(...statusRows.map((row) => row.count), 1)
  const collectionPercent = Math.min(100, Number(summary?.collectionPercent || 0))

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fee Analytics"
        subtitle="Track fee demand, collections, adjustments, and overdue trends from one dedicated admin view."
        chips={[
          selectedYear || 'All academic years',
          summary ? `${summary.totalStudents || 0} student accounts` : 'Loading analytics',
          summary ? `${defaulters.length} current defaulters` : 'Loading defaulters',
        ]}
        actions={
          <>
            <button onClick={() => navigate('/fees/accounts')} className="btn-secondary">
              <CreditCard className="w-4 h-4" /> Fee Accounts
            </button>
            <button onClick={fetchAnalytics} className="btn-secondary">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </>
        }
      />

      <div className="card p-4 sm:p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">Academic Year Filter</p>
            <p className="text-xs text-gray-500 mt-1">Switch between billing years without leaving the analytics view.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedYear('')}
              className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                selectedYear === ''
                  ? 'border-primary-600 bg-primary-50 text-primary-700'
                  : 'border-gray-200 text-gray-600 hover:border-primary-200'
              }`}
            >
              All Years
            </button>
            {years.map((year) => (
              <button
                key={year}
                onClick={() => setSelectedYear(year)}
                className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                  selectedYear === year
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-gray-200 text-gray-600 hover:border-primary-200'
                }`}
              >
                {year}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card p-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
        </div>
      ) : !summary ? (
        <div className="empty-state card">
          <Percent className="w-10 h-10 text-gray-200" />
          <p className="empty-state-title">No analytics data yet</p>
          <p className="empty-state-copy">Create fee structures and generate student accounts to unlock fee analytics.</p>
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {statCards.map((card) => {
              const Icon = card.icon
              return (
                <div key={card.label} className="card p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${card.color}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <Sparkles className="w-4 h-4 text-gray-300" />
                  </div>
                  <p className="text-sm font-medium text-gray-500 mt-4">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                  <p className="text-xs text-gray-400 mt-2">{card.note}</p>
                </div>
              )
            })}
          </div>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_380px]">
            <div className="space-y-6">
              <div className="card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Collection Meter</h3>
                    <p className="text-sm text-gray-500 mt-1">How much of the billed fee demand has already been collected.</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-sm font-semibold ${
                    collectionPercent >= 80
                      ? 'bg-green-50 text-green-700'
                      : collectionPercent >= 50
                      ? 'bg-amber-50 text-amber-700'
                      : 'bg-red-50 text-red-600'
                  }`}>
                    {collectionPercent.toFixed(1)}%
                  </span>
                </div>

                <div className="mt-6 space-y-3">
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Collected</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(summary.totalCollected)}</span>
                  </div>
                  <div className="h-4 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        collectionPercent >= 80
                          ? 'bg-green-500'
                          : collectionPercent >= 50
                          ? 'bg-amber-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${collectionPercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Total demand: {formatCurrency(summary.totalDemand)}</span>
                    <span>Outstanding: {formatCurrency(summary.totalPending)}</span>
                  </div>
                </div>
              </div>

              <div className="card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Status Distribution</h3>
                    <p className="text-sm text-gray-500 mt-1">A quick read on how student fee accounts are spread across payment states.</p>
                  </div>
                  <button onClick={() => navigate('/fees/defaulters')} className="text-sm font-semibold text-primary-600 hover:text-primary-700">
                    View defaulters
                  </button>
                </div>

                <div className="mt-6 space-y-4">
                  {statusRows.map((row) => (
                    <div key={row.label} className="grid grid-cols-[110px_minmax(0,1fr)_56px] items-center gap-3">
                      <span className="text-sm text-gray-600">{row.label}</span>
                      <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${row.color}`}
                          style={{ width: `${(row.count / maxStatusCount) * 100}%` }}
                        />
                      </div>
                      <span className="text-right text-sm font-semibold text-gray-900">{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-6 lg:grid-cols-2">
                <div className="card p-5">
                  <h3 className="text-lg font-semibold text-gray-900">Outstanding Breakdown</h3>
                  <div className="mt-5 space-y-3">
                    {[
                      { label: 'Gross demand', value: summary.totalDemand, tone: 'text-primary-700 bg-primary-50' },
                      { label: 'Net demand', value: summary.netDemand ?? summary.totalDemand, tone: 'text-violet-700 bg-violet-50' },
                      { label: 'Collected', value: summary.totalCollected, tone: 'text-green-700 bg-green-50' },
                      { label: 'Pending / overdue', value: summary.totalPending, tone: 'text-red-600 bg-red-50' },
                    ].map((item) => (
                      <div key={item.label} className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
                        <span className="text-sm text-gray-600">{item.label}</span>
                        <span className={`rounded-full px-3 py-1 text-sm font-semibold ${item.tone}`}>{formatCurrency(item.value)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="card p-5">
                  <h3 className="text-lg font-semibold text-gray-900">Adjustments Impact</h3>
                  <p className="text-sm text-gray-500 mt-1">Discounts, concessions, and fines applied across fee accounts.</p>
                  <div className="mt-5 space-y-3">
                    {[
                      { label: 'Discount', value: summary.totalDiscount || 0, tone: 'text-green-700 bg-green-50', icon: Percent },
                      { label: 'Concession', value: summary.totalConcession || 0, tone: 'text-blue-700 bg-blue-50', icon: Ribbon },
                      { label: 'Fine Added', value: summary.totalFine || 0, tone: 'text-red-600 bg-red-50', icon: AlertCircle },
                    ].map((item) => {
                      const Icon = item.icon
                      return (
                        <div key={item.label} className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-white px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${item.tone}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <span className="text-sm text-gray-600">{item.label}</span>
                          </div>
                          <span className={`rounded-full px-3 py-1 text-sm font-semibold ${item.tone}`}>
                            {formatCurrency(item.value)}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="card p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Top Defaulters</h3>
                    <p className="text-sm text-gray-500 mt-1">Highest outstanding accounts in the current filtered fee view.</p>
                  </div>
                  <button onClick={() => navigate('/fees/defaulters')} className="text-sm font-semibold text-primary-600 hover:text-primary-700">
                    View all
                  </button>
                </div>

                <div className="mt-5 space-y-3">
                  {defaulters.length === 0 ? (
                    <div className="rounded-2xl border border-green-100 bg-green-50 px-4 py-6 text-center">
                      <p className="text-sm font-semibold text-green-700">No defaulters right now</p>
                      <p className="text-xs text-green-600 mt-1">That fee cycle looks healthy.</p>
                    </div>
                  ) : (
                    defaulters.slice(0, 5).map((defaulter) => {
                      const name = `${defaulter.studentId?.firstName || ''} ${defaulter.studentId?.lastName || ''}`.trim() || 'Student'
                      const classLabel = defaulter.classId?.name
                        ? `${defaulter.classId.name}${defaulter.classId.section ? ` — ${defaulter.classId.section}` : ''}`
                        : 'No class'

                      return (
                        <button
                          key={defaulter._id || defaulter.id}
                          onClick={() => navigate(`/fees/accounts/${defaulter._id || defaulter.id}`)}
                          className="w-full rounded-2xl border border-gray-100 bg-white px-4 py-3 text-left transition-colors hover:border-primary-200 hover:bg-primary-50/40"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{name}</p>
                              <p className="text-xs text-gray-500 mt-1 truncate">
                                {classLabel}
                                {defaulter.studentId?.admissionNo ? ` • ${defaulter.studentId.admissionNo}` : ''}
                              </p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                          </div>
                          <div className="mt-3 flex items-center justify-between gap-3 text-sm">
                            <span className="rounded-full bg-red-50 px-2.5 py-1 font-semibold text-red-600">
                              {formatCurrency(defaulter.overdueAmount || defaulter.outstanding || 0)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {defaulter.overdueCount || 0} overdue installment{defaulter.overdueCount === 1 ? '' : 's'}
                            </span>
                          </div>
                        </button>
                      )
                    })
                  )}
                </div>
              </div>

              <div className="card p-5">
                <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
                <div className="mt-4 space-y-2">
                  {[
                    { label: 'Review fee accounts', to: '/fees/accounts' },
                    { label: 'Record offline payment', to: '/fees/collection' },
                    { label: 'Apply adjustments', to: '/fees/discounts' },
                    { label: 'Manage fee structures', to: '/fees/structures' },
                  ].map((action) => (
                    <button
                      key={action.to}
                      onClick={() => navigate(action.to)}
                      className="w-full rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-700 transition-colors hover:border-primary-200 hover:bg-primary-50 hover:text-primary-700"
                    >
                      <span className="flex items-center justify-between gap-3">
                        {action.label}
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
